import React, {useState, useCallback, useEffect, useRef} from 'react';
import styles from './StepStart.module.scss';
import {
  PreScoringView,
  CheckCounterpartiesView,
  CounterpartiesCounterView,
  RequirementsView,
  BackToTop
} from './subviews';
import scales from 'assets/images/svg/scales.svg';
import chess from 'assets/images/svg/chess.svg';
import magnifier from 'assets/images/svg/magnifier.svg';
import stepApprovedIcon from 'assets/images/svg/step-approved.svg';
import {Button, Image, Loader} from '../../../../../../../components';
import {useWizardContext} from '../../../context';
import {WizardStep} from '../../../types';
import {createBorrowingOnboardingManager} from '../../../../../../../di';
import type {ScoresCount} from '../../../../../../../api-client';

interface CardConfig {
  id: string;
  title: string;
  description: string;
  icon?: string;
}

const cardsData: CardConfig[] = [
  {
    id: 'counterparties',
    title: 'Проверить своих контрагентов',
    description: 'Получите детальный отчет о своих контрагентах от нашего партнера - компании Rescore, входящего в группу компаний Кономика',
    icon: magnifier
  },
  {
    id: 'requirements',
    title: 'Узнать требования к заемщику и обеспечению',
    description: 'Узнайте о стоп факторах для нас',
    icon: scales
  },
  {
    id: 'platform',
    title: 'Что предлагает платформа',
    description: 'Варианты/условия сделок на платформе',
    icon: chess,
  },
];

const StepStart = () => {
  const {backendCurrentStep, setActiveStep, isInitialDataLoaded} = useWizardContext();
  const isPreScoringCompleted = backendCurrentStep != null && backendCurrentStep > WizardStep.START;
  const isLoading = !isInitialDataLoaded || backendCurrentStep === null;

  const counterpartiesSectionRef = useRef<HTMLElement>(null);
  const requirementsSectionRef = useRef<HTMLElement>(null);

  const [scoresCount, setScoresCount] = useState<ScoresCount | null>(null);
  const [scoresCountLoading, setScoresCountLoading] = useState(true);
  const fetchScoresCount = useCallback(async () => {
    setScoresCountLoading(true);
    try {
      const manager = await createBorrowingOnboardingManager();
      const data = await manager.getScoresCount();
      setScoresCount(data);
    } catch {
      setScoresCount(null);
    } finally {
      setScoresCountLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScoresCount();
  }, [fetchScoresCount]);

  const scrollToTop = () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const scrollToSection = (cardId: string) => {
    if (cardId === 'counterparties' && counterpartiesSectionRef.current) {
      counterpartiesSectionRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
    if (cardId === 'requirements' && requirementsSectionRef.current) {
      requirementsSectionRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  };

  const renderApprovedCard = () => (
    <div className={styles.approvedCardContainer}>
      <div className={styles.approvedCard}>
        <Image src={stepApprovedIcon} className={styles.approvedIcon} alt="" aria-hidden />
        <div className={styles.approvedContent}>
          <h2 className={styles.approvedTitle}>Ваша pre-scoring заявка одобрена</h2>
          <p className={styles.approvedText}>
              Данные Вашей анкеты приняты. Вы можете перейти к следующему шагу.
          </p>
        </div>
        <Button
          type="button"
          className={styles.approvedButton}
          onClick={() => setActiveStep(WizardStep.VERIFICATION)}
        >
            Перейти к следующему шагу
        </Button>
      </div>
    </div>

  );

  const renderInfoCard = () => (
    <div className={styles.infoCard}>
      <div className={styles.infoIcon}>
        <span>!</span>
      </div>
      <p className={styles.infoText}>
        Вы находитесь на первом этапе получения займа, на котором необходимо пройти pre-scoring
      </p>
    </div>
  );

  const renderCards = () => (
    <div className={styles.cardsStack}>
      {cardsData.map((card) => (
        <div
          key={card.id}
          className={styles.card}
          role="button"
          tabIndex={0}
          onClick={() => scrollToSection(card.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              scrollToSection(card.id);
            }
          }}
        >
          <Image src={card.icon} className={styles.cardIcon} alt={card.title} />
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>{card.title}</div>
            <div className={styles.cardDescription}>{card.description}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderMainContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loaderWrapper}>
          <Loader size="large" overlay={false} />
        </div>
      );
    }
    if (isPreScoringCompleted) {
      return renderApprovedCard();
    }
    return <PreScoringView />;
  };

  return (
    <div className={styles.stepStart}>
      <div className={styles.mobileLayout}>
        {!isLoading && !isPreScoringCompleted && renderInfoCard()}
        {renderMainContent()}
        {!isLoading && (
          <div className={styles.mobileCardsSection}>
            {renderCards()}
          </div>
        )}
      </div>

      <div className={styles.desktopLayout}>
        <div className={styles.leftColumn}>
          {renderMainContent()}
        </div>

        {!isLoading && (
          <div className={styles.rightColumn}>
            {renderInfoCard()}
            {renderCards()}
          </div>
        )}
      </div>

      <section ref={counterpartiesSectionRef} className={styles.section} aria-label="Проверить своих контрагентов">
        <BackToTop onClick={scrollToTop} />
        <div className={styles.counterpartiesRow}>
          <CheckCounterpartiesView onCounterpartyScored={fetchScoresCount} />
          <CounterpartiesCounterView
            availableCount={scoresCount?.left}
            isLoading={scoresCountLoading}
          />
        </div>
      </section>

      <section ref={requirementsSectionRef} className={styles.section} aria-label="Требования к заемщикам">
        <BackToTop onClick={scrollToTop} />
        <RequirementsView />
      </section>
    </div>
  );
};

export default StepStart;
